// Control listing edition assigns its roles to four different things — a user,
// a permission group, a branch or a team — and the backend keeps them in four
// separate lists. The UI renders them as one list, so both the stored payload
// and the search results are normalized to the same `assignee` shape:
//
//   { key, kind, id, name, image, branchName, subtitle, memberCount,
//     branchIds, ownBranchId }
//
// `key` is what React keys on and what the UI uses to tell two assignees apart;
// ids only collide across kinds, never inside one.
//
// `branchIds` is a portfolio manager's branch scope, where an empty list means
// "all branches" — the backend sends `null` for that, never `[]`.

export const ASSIGNEE_KIND_USER = 'user';
export const ASSIGNEE_KIND_GROUP = 'group';
export const ASSIGNEE_KIND_BRANCH = 'branch';
export const ASSIGNEE_KIND_TEAM = 'team';

// Each kind travels to the backend under its own field, and the endpoint
// rejects a body carrying more than one of them.
const ID_FIELD_BY_KIND = {
  [ASSIGNEE_KIND_USER]: 'user_id',
  [ASSIGNEE_KIND_GROUP]: 'company_group_id',
  [ASSIGNEE_KIND_BRANCH]: 'branch_id',
  [ASSIGNEE_KIND_TEAM]: 'team_id',
};

export const toAssigneeKey = (kind, id) => `${kind}:${id}`;

export const toAssigneeParams = ({ kind, id }) => ({
  [ID_FIELD_BY_KIND[kind]]: String(id),
});

const toAssignee = ({
  kind,
  id,
  name,
  image,
  branchName,
  subtitle,
  memberCount,
  branchIds,
  ownBranchId,
}) => ({
  key: toAssigneeKey(kind, id),
  kind,
  id,
  name: name || '',
  image: image || '',
  branchName: branchName || '',
  subtitle: subtitle || '',
  memberCount: typeof memberCount === 'number' ? memberCount : null,
  branchIds: branchIds || [],
  ownBranchId: ownBranchId ?? null,
});

// `{ users, groups, branches, teams }` as returned by the portfolio managers
// and users-without-supervision endpoints.
export const adaptAssigneeList = (response = {}) => {
  const { users = [], groups = [], branches = [], teams = [] } = response || {};

  return [
    ...users.map((user) =>
      toAssignee({
        kind: ASSIGNEE_KIND_USER,
        id: user.id,
        name: user.full_name,
        image: user.profile_image,
        branchName: user.subtitle,
        branchIds: user.branch_ids,
        ownBranchId: user.own_branch_id,
      })
    ),
    ...branches.map((branch) =>
      toAssignee({
        kind: ASSIGNEE_KIND_BRANCH,
        id: branch.branch_id,
        name: branch.name,
        image: branch.profile_image,
        subtitle: branch.subtitle,
        memberCount: branch.user_count,
      })
    ),
    ...teams.map((team) =>
      toAssignee({
        kind: ASSIGNEE_KIND_TEAM,
        id: team.team_id,
        name: team.name,
        image: team.profile_image,
        subtitle: team.subtitle,
        memberCount: team.user_count,
      })
    ),
    ...groups.map((group) =>
      toAssignee({
        kind: ASSIGNEE_KIND_GROUP,
        id: group.company_group_id,
        name: group.name,
        subtitle: group.subtitle,
        memberCount: group.user_count,
        branchIds: group.branch_ids,
      })
    ),
  ];
};

// The typeahead fans out to three endpoints with three different shapes:
// `/api3/agent/` answers users and permission groups together, while branches
// and teams come paginated under `results`.
export const adaptAssigneeSearchResults = ({
  agents = {},
  branches = {},
  teams = {},
} = {}) => [
  ...(agents.users || []).map((user) =>
    toAssignee({
      kind: ASSIGNEE_KIND_USER,
      id: user.id,
      name: user.full_name,
      image: user.profile_image,
      branchName: user.branch?.name,
    })
  ),
  ...(branches.results || []).map((branch) =>
    toAssignee({
      kind: ASSIGNEE_KIND_BRANCH,
      id: branch.id,
      name: branch.name,
      image: branch.logo,
      memberCount: branch.members_count ?? branch.user_count,
    })
  ),
  ...(teams.results || []).map((team) =>
    toAssignee({
      kind: ASSIGNEE_KIND_TEAM,
      id: team.id,
      name: team.name,
      image: team.logo,
      memberCount: team.members_count ?? team.user_count,
    })
  ),
  ...(agents.company_groups || []).map((group) =>
    toAssignee({
      kind: ASSIGNEE_KIND_GROUP,
      id: group.id,
      name: group.name,
      memberCount: group.user_count,
    })
  ),
];
